// CreateCategories.js
// Componente para crear, editar, eliminar y listar categorías con paginación
import React, { useState, useEffect } from 'react';
import './CreateAuthors.css';
import './HomeAdmin.css';
import AdminNavbar from './AdminNavbar';

const API_URL = 'https://backend-zonamicroondas.vercel.app/api'; // ajústalo si tu backend es otro
const PAGE_SIZE = 10; // <-- mostrar 5 categorías por página

/** Genera slug limpio desde un texto */
const generateSlug = (text = '') =>
  text
    .toLowerCase()
    .trim()
    .replace(/á/g, 'a')
    .replace(/é/g, 'e')
    .replace(/í/g, 'i')
    .replace(/ó/g, 'o')
    .replace(/ú/g, 'u')
    .replace(/ñ/g, 'n')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export default function CreateCategories() {
  /* ===== ESTADOS ===== */
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [originalCategory, setOriginalCategory] = useState(null); // para comparar en update

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    parent_id: null,
    position: '',
    description: ''
  });

  // PAGINACIÓN
  const [page, setPage] = useState(0);

  useEffect(() => {
    // cargar y resetear a primera página
    (async () => {
      await loadCategories();
      setPage(0);
    })();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/categories`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        // Si tu backend no incluye 'parent_name', podemos calcularlo aquí para mostrar en la tabla
        const list = Array.isArray(data.data) ? data.data.slice() : [];

        // build map id -> name for parent display
        const nameById = new Map(list.map(c => [c.id, c.name]));

        const enriched = list.map(c => ({
          ...c,
          parent_name: c.parent_id ? nameById.get(c.parent_id) || '-' : '-'
        }));

        // ordenar por position (si existe) y luego por name
        enriched.sort((a, b) => {
          const aPos = typeof a.position !== 'undefined' && a.position !== null ? Number(a.position) : Number.MAX_SAFE_INTEGER;
          const bPos = typeof b.position !== 'undefined' && b.position !== null ? Number(b.position) : Number.MAX_SAFE_INTEGER;
          if (aPos !== bPos) return aPos - bPos;
          return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        });

        setCategories(enriched);
      } else {
        setCategories([]);
        setError('No se pudieron cargar las categorías');
      }
    } catch (err) {
      setError('Error al cargar categorías: ' + (err.message || err));
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Generar slug automático solo si el campo es name y el slug actual está vacío (o estamos creando)
    if (name === 'name' && !editingId && !formData.slug) {
      const newSlug = generateSlug(value);
      setFormData(prev => ({ ...prev, name: value, slug: newSlug }));
      return;
    }

    // Si se cambia parent_id desde select, convertir a number o null
    if (name === 'parent_id') {
      const parsed = value === '' ? null : Number(value);
      setFormData(prev => ({ ...prev, parent_id: parsed }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const buildPayloadForCreate = () => {
    const payload = {
      name: formData.name,
      slug: formData.slug || generateSlug(formData.name),
      parent_id: formData.parent_id === null ? null : formData.parent_id,
      position: formData.position ? Number(formData.position) : undefined,
      description: formData.description || null
    };
    Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
    return payload;
  };

  const buildPayloadForUpdate = () => {
    const payload = {};
    if (!originalCategory) return payload;

    if (formData.name && formData.name !== originalCategory.name) payload.name = formData.name;
    const finalSlug = formData.slug || generateSlug(formData.name || originalCategory.name || '');
    if (finalSlug && finalSlug !== originalCategory.slug) payload.slug = finalSlug;

    const origParent = originalCategory.parent_id === null ? null : originalCategory.parent_id;
    const newParent = formData.parent_id === null ? null : formData.parent_id;
    if (('parent_id' in formData) && newParent !== origParent) payload.parent_id = newParent;

    const origPosition = originalCategory.position === null || originalCategory.position === undefined
      ? ''
      : String(originalCategory.position);
    if (formData.position !== '' && formData.position !== origPosition) payload.position = Number(formData.position);

    if (('description' in formData) && (formData.description !== (originalCategory.description || ''))) {
      payload.description = formData.description || null;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (!formData.name || formData.name.trim() === '') {
        setError('El nombre es obligatorio.');
        setLoading(false);
        return;
      }

      if (editingId && formData.parent_id !== null && Number(formData.parent_id) === Number(editingId)) {
        setError('parent_id no puede ser igual al id de la categoría.');
        setLoading(false);
        return;
      }

      if (formData.parent_id !== null && !categories.find(c => c.id === Number(formData.parent_id))) {
        setError('El padre seleccionado no existe.');
        setLoading(false);
        return;
      }

      let payload;
      let method;
      let url;

      if (editingId) {
        payload = buildPayloadForUpdate();
        if (Object.keys(payload).length === 0) {
          setError('Debes cambiar al menos un campo para actualizar.');
          setLoading(false);
          return;
        }
        method = 'PUT';
        url = `${API_URL}/categories/${editingId}`;
      } else {
        payload = buildPayloadForCreate();
        method = 'POST';
        url = `${API_URL}/categories`;
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        const msg = errBody && errBody.error ? errBody.error : `HTTP error ${res.status}`;
        throw new Error(msg);
      }

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Operación fallida');
      }

      setSuccess(editingId ? 'Categoría actualizada exitosamente' : 'Categoría creada exitosamente');

      // limpiar formulario y recargar (ir a primera página)
      setFormData({ name: '', slug: '', parent_id: null, position: '', description: '' });
      setEditingId(null);
      setOriginalCategory(null);
      setShowForm(false);
      await loadCategories();
      setPage(0);

      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      setError(err.message || 'Error al guardar categoría');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setOriginalCategory(category);
    setShowForm(true);
    setFormData({
      name: category.name || '',
      slug: category.slug || '',
      parent_id: category.parent_id === undefined ? null : category.parent_id,
      position: category.position === undefined || category.position === null ? '' : String(category.position),
      description: category.description || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setOriginalCategory(null);
    setShowForm(false);
    setFormData({ name: '', slug: '', parent_id: null, position: '', description: '' });
  };

  const handleDelete = async (id) => {
    const ok = window.confirm('¿Eliminar esta categoría? Esta acción no se puede deshacer.');
    if (!ok) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/categories/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        const msg = errBody && errBody.error ? errBody.error : `HTTP error ${res.status}`;
        throw new Error(msg);
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error al eliminar');

      setSuccess('Categoría eliminada');
      // recargar y ajustar pagina si hace falta
      await loadCategories();
      setPage(0);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err.message || 'Error al eliminar categoría');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setShowForm(true);
    setEditingId(null);
    setOriginalCategory(null);
    setFormData({ name: '', slug: '', parent_id: null, position: '', description: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ---------- PAGINACIÓN ----------
  const totalPages = Math.max(1, Math.ceil(categories.length / PAGE_SIZE));
  const start = page * PAGE_SIZE;
  const pageItems = categories.slice(start, start + PAGE_SIZE);

  const handlePrev = () => setPage(p => Math.max(0, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages - 1, p + 1));

  const rangeStart = categories.length === 0 ? 0 : start + 1;
  const rangeEnd = Math.min(start + PAGE_SIZE, categories.length);

  // ---------- RENDER ----------
  return (
    <div className="home-container">
      <AdminNavbar />

      {/* ===== CONTENIDO ===== */}
      <main className="main-content">
        <div className="create-authors">
          {showForm && (
            <>
              <div className="create-authors-header">
                <h2>{editingId ? 'Editar Categoría' : 'Crear Categoría'}</h2>
              </div>

              {error && <div className="alert alert-error">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <form className="author-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 2 }}>
                    <label>Nombre *</label>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Nombre de la categoría"
                      required
                    />
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Posición</label>
                    <input
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      placeholder="Orden (número)"
                      type="number"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Slug (editable)</label>
                    <input
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      placeholder="slug-ejemplo (se genera automáticamente)"
                    />
                    <small>Si lo dejas vacío se generará automáticamente desde el nombre.</small>
                  </div>

                  <div className="form-group">
                    <label>Padre</label>
                    <select
                      name="parent_id"
                      value={formData.parent_id === null ? '' : String(formData.parent_id)}
                      onChange={handleInputChange}
                    >
                      <option value="">(Sin padre)</option>
                      {categories
                        .filter(c => !editingId || c.id !== editingId) // evitar seleccionar a sí mismo
                        .map(c => (
                          <option key={c.id} value={c.id}>
                            {c.parent_name ? `${c.parent_name} / ${c.name}` : c.name}
                          </option>
                        ))}
                    </select>
                    <small>Selecciona una categoría padre (opcional).</small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Descripción</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Descripción breve"
                    rows="3"
                  />
                </div>

                <div className="form-actions">
                  {editingId ? (
                    <>
                      <button type="button" className="btn btn-secondary" onClick={handleCancelEdit} disabled={loading}>
                        Cancelar
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Guardando...' : 'Actualizar Categoría'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="btn btn-secondary" onClick={handleCancelEdit} disabled={loading}>
                        Cancelar
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Creando...' : 'Crear Categoría'}
                      </button>
                    </>
                  )}
                </div>
              </form>

              <hr />
            </>
          )}

          <div className="authors-list">
            <div className="list-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3>Categorías Registradas</h3>
              {!showForm && (
                <button className="btn btn-primary" onClick={handleCreateNew}>
                  Crear Nueva Categoría
                </button>
              )}
            </div>

            {loading && categories.length === 0 ? (
              <div className="loading">Cargando categorías...</div>
            ) : categories.length === 0 ? (
              <div className="no-data">No hay categorías</div>
            ) : (
              <>
                <table className="authors-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Slug</th>
                      <th>Padre</th>
                      <th>Posición</th>
                      <th>Descripción</th>
                      <th className="centered">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="no-data">No hay categorías en esta página</td>
                      </tr>
                    ) : (
                      pageItems.map(c => (
                        <tr key={c.id}>
                          <td>{c.name}</td>
                          <td>{c.slug}</td>
                          <td>{c.parent_name || '-'}</td>
                          <td>{(typeof c.position !== 'undefined' && c.position !== null) ? c.position : '-'}</td>
                          <td className="small-cell">
                            {c.description ? (c.description.length > 80 ? `${c.description.slice(0, 77)}...` : c.description) : '-'}
                          </td>
                          <td className="centered">
                            <button className="btn btn-secondary" onClick={() => handleEdit(c)}>Editar</button>
                            <button
                              className="btn btn-danger"
                              onClick={() => handleDelete(c.id)}
                              style={{ marginLeft: 8 }}
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* PAGINADOR */}
                {categories.length > PAGE_SIZE && (
                  <div className="pagination" style={{
                    marginTop: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12
                  }}>
                    <button
                      className="page-btn"
                      onClick={handlePrev}
                      disabled={page === 0}
                      aria-label="Anterior"
                    >
                      &lt;
                    </button>

                    <div className="page-info" aria-live="polite" style={{ minWidth: 220, textAlign: 'center' }}>
                      Categorías {rangeStart}-{rangeEnd} de {categories.length} (página {page + 1} de {totalPages})
                    </div>

                    <button
                      className="page-btn"
                      onClick={handleNext}
                      disabled={page >= totalPages - 1}
                      aria-label="Siguiente"
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
